import { Box, styled } from '@mui/material';
import Color from 'color';
import _ from 'lodash';
import { CurrencyAmount } from '@cowbell/ui/core';
import { CoverageItemContainer } from '../coverages/line-items/CoverageLineItem';
import type { RateModifier } from '../../../../api/types/rater-x-preset';
import { Show } from '../../../../../../../../components/Show';
import UWRatingOptionLineItemName from './UwRatingOptionLineItemName';
import ModifierInputFactory from './ModifierInputFactory';
import StagedRowActions from '../../draft/components/StagedRowActions';
import StagedWas from '../../draft/components/StagedWas';
import {
  modifierInclusionPath,
  modifierPath,
  modifierRowPaths,
} from '../../draft/stagedFields';
import { useLocale } from '../../../../providers/LocaleProvider';
import { deriveCurrencyFormat } from '../../../../../../../../i18n/currencies';
import type { Languages } from '../../../../../../../../i18n/i18n.language-config';
import UnderwriterSupplementalInfo from '../../shared/UnderwriterSupplementalInfo';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';
import {
  getUnderwriteQuoteCurrency,
  isCoverageEndorsementsState,
} from '../../types';
import DeltaPricing from '../../../../../../policies/prime-x/endorsements/components/coverages/DeltaPricing';
import UnderwriterLineItemActions from '../../shared/UnderwriterLineItemActions';

/**
 * Renders the fee/delta cell: a `DeltaPricing` in the MTE/coverage-endorsements flow (which
 * always has a policy currency to color/sign against), otherwise the flat modifier fee via
 * `currency` when available, falling back to the locale-derived format only if that's
 * genuinely unavailable.
 */
const renderFeeAmount = (
  isMTEFlow: boolean,
  premiumDelta: number | null | undefined,
  feeValue: number | null | undefined,
  currency: string | null | undefined,
  language: Languages
) => {
  if (isMTEFlow) {
    return <DeltaPricing price={premiumDelta} currency={currency} />;
  }
  if (currency != null) {
    return (
      <CurrencyAmount amount={feeValue} currency={currency} placeholder="N/A" />
    );
  }
  return deriveCurrencyFormat(language, feeValue, { placeholder: 'N/A' });
};

const UnderwriterModifier = (props: UnderwriterModifierProps) => {
  const locale = useLocale();
  const context = useUnderwriterContext();
  const ratingOptionsState = context.state.ratingOptions[props.id];
  // Policy currency in the MTE/coverage-endorsements flow, the quote's own currency
  // otherwise -- shared by every money render below so the fee, delta, and running
  // aggregate in this card never disagree on currency symbol.
  const currency = isCoverageEndorsementsState(context.state)
    ? context.state.policy.currency
    : getUnderwriteQuoteCurrency(context.state);

  const handleChange = (params: Partial<RateModifier>) => {
    context.handlers.setRatingOption({
      ...params,
      id: props.id,
      isPegValue: true,
    });
  };

  return (
    <>
      <CoverageItemContainer>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            sx={{
              flexWrap: 'nowrap',
              minWidth: 0,
              width: '25%',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: '50%',
                minWidth: 0,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <UWRatingOptionLineItemName
                displayCheckBox={props.isEditable}
                isSelected={ratingOptionsState.isSelected}
                onClick={(isSelected) =>
                  handleChange({
                    isSelected,
                  })
                }
                supplementalInfo={
                  <UnderwriterSupplementalInfo
                    ml={props.isEditable ? '-1rem' : 0}
                    appliedRules={props.appliedRules}
                  />
                }
              >
                {props.name}
              </UWRatingOptionLineItemName>
            </Box>
            <UnderwriterLineItemActions
              isPegValue={ratingOptionsState.isPegValue}
              warnings={props.warnings}
              errors={props.validationErrors}
              referralReasons={props.referralReasons}
              referralReasonsForEdit={ratingOptionsState.referralReasonsForEdit}
              referralStrategyForEdit={
                ratingOptionsState.referralStrategyForEdit
              }
              onUpdatePegValue={(isPegValue) => {
                context.handlers.setRatingOption({
                  isPegValue,
                  id: props.id,
                });
              }}
              onUpdateReferralStrategy={(referralStrategyForEdit) => {
                context.handlers.setRatingOption({
                  referralStrategyForEdit,
                  id: props.id,
                });
              }}
            />
          </Box>

          {/* The row's props and its inclusion: one pair resolves both. */}
          <StagedRowActions paths={modifierRowPaths(props.id)} />
          {/* Column-wrapped: this row is `space-between`, so a bare sibling
              becomes a flex item of its own and the struck value floats in the
              gap between the name and the input instead of sitting under the
              control. */}
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            {props.rules && !_.isEmpty(props.rules) && props.value ? (
              <ModifierInputFactory
                rules={ratingOptionsState.rules}
                value={ratingOptionsState.value}
                id={props.id}
                type={props.type}
                handleChange={handleChange}
                ratingDetails={props.ratingDetails}
                productType={context.state.productType}
              />
            ) : (
              <Box pr={2}>
                {renderFeeAmount(
                  isCoverageEndorsementsState(context.state),
                  props.ratingDetails.premiumDelta?.amount,
                  props.value?.props?.fee?.value,
                  currency,
                  locale.language
                )}
              </Box>
            )}
            <StagedWas paths={[modifierPath(props.id)]} />
            {/* The checkbox beside the name shows the new state; this is the
                old one, where the value's own old figure goes. */}
            <StagedWas paths={[modifierInclusionPath(props.id)]} prefixed />
          </Box>
        </Box>
      </CoverageItemContainer>

      <Show when={Boolean(props.rules && props.value?.props?.factor)}>
        <RunningAggregateContainer>
          <i>
            Running Aggregate{' '}
            {props.ratingDetails.aggregatePremiumDelta && 'delta'} after{' '}
            {props.name}:{' '}
            {(() => {
              const aggregateAmount = props.ratingDetails.aggregatePremiumDelta
                ? props.ratingDetails.aggregatePremiumDelta.amount
                : props.ratingDetails.aggregatePremium;
              return currency != null ? (
                <CurrencyAmount amount={aggregateAmount} currency={currency} />
              ) : (
                deriveCurrencyFormat(locale.language, aggregateAmount)
              );
            })()}
          </i>
        </RunningAggregateContainer>
      </Show>
    </>
  );
};

export default UnderwriterModifier;

type UnderwriterModifierProps = {
  id: string;
} & RateModifier;

const RunningAggregateContainer = styled(Box)(({ theme }) => {
  return {
    backgroundColor: Color(theme.palette.background.default)
      .lighten(theme.palette.mode === 'dark' ? 0.25 : 0.03)
      .toString(),
    textAlign: 'right',
    width: '100%',
    padding: '0.35rem',
    paddingRight: '1.5rem',
    fontWeight: 600,
  };
});
